import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';

interface Note {
  id: number;
  content: string;
  created_by: number;
  vorname: string;
  nachname: string;
  page_context: string | null;
  created_at: string;
  replies?: Note[];
  latest_activity?: string;
}

interface BoardNotesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  pageContext: string;
  onUnreadCountChange?: (count: number) => void;
}

const BoardNotesPanel: React.FC<BoardNotesPanelProps> = ({ isOpen, onClose, pageContext, onUnreadCountChange }) => {
  const { user } = useContext(AuthContext);
  const [notes, setNotes] = useState<Note[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);

  const loadNotes = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/board-notes');
      setNotes(res.data || []);
    } catch (err) {
      console.error('Nachrichten laden fehlgeschlagen:', err);
    }
  }, []);

  const markAsRead = useCallback(async (noteIds: number[]) => {
    if (noteIds.length === 0) return;
    try {
      await apiClient.post('/api/board-notes/mark-read', { note_ids: noteIds });
      if (onUnreadCountChange) {
        const res = await apiClient.get('/api/board-notes/unread-count');
        onUnreadCountChange(res.data?.count || 0);
      }
    } catch (err) {
      console.error('Mark-Read fehlgeschlagen:', err);
    }
  }, [onUnreadCountChange]);

  useEffect(() => {
    if (!isOpen) return;
    loadNotes().then(() => {
      setTimeout(async () => {
        try {
          const res = await apiClient.get('/api/board-notes');
          const allNotes: Note[] = res.data || [];
          const allIds: number[] = [];
          allNotes.forEach(n => {
            allIds.push(n.id);
            (n.replies || []).forEach(r => allIds.push(r.id));
          });
          await markAsRead(allIds);
        } catch {}
      }, 500);
    });
  }, [isOpen, loadNotes, markAsRead]);

  // Focus reply textarea when opening reply
  useEffect(() => {
    if (replyingTo && replyTextareaRef.current) {
      replyTextareaRef.current.focus();
    }
  }, [replyingTo]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await apiClient.post('/api/board-notes', {
        content: newMessage.trim(),
        page_context: pageContext || null
      });
      setNewMessage('');
      await loadNotes();
    } catch (err) {
      console.error('Nachricht senden fehlgeschlagen:', err);
    } finally {
      setSending(false);
    }
  };

  const handleReply = async (parentId: number) => {
    if (!replyMessage.trim() || sending) return;
    setSending(true);
    try {
      await apiClient.post('/api/board-notes', {
        content: replyMessage.trim(),
        parent_id: parentId,
        page_context: pageContext || null
      });
      setReplyMessage('');
      setReplyingTo(null);
      await loadNotes();
    } catch (err) {
      console.error('Antwort senden fehlgeschlagen:', err);
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    try {
      await apiClient.delete(`/api/board-notes/${noteId}`);
      await loadNotes();
    } catch (err) {
      console.error('Nachricht löschen fehlgeschlagen:', err);
    }
  };

  const getInitials = (vorname: string, nachname: string) => {
    return `${(vorname || '')[0] || ''}${(nachname || '')[0] || ''}`.toUpperCase();
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMin / 60);
    const diffD = Math.floor(diffH / 24);

    if (diffMin < 1) return 'gerade eben';
    if (diffMin < 60) return `vor ${diffMin} Min.`;
    if (diffH < 24) return `vor ${diffH} Std.`;
    if (diffD < 7) return `vor ${diffD} ${diffD === 1 ? 'Tag' : 'Tagen'}`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, action: () => void) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      action();
    }
  };

  if (!isOpen) return null;

  const isOwn = (note: Note) => note.created_by === user?.mg_nr;

  // Render a single message bubble
  const renderBubble = (note: Note, isReply: boolean) => {
    const own = isOwn(note);

    if (isReply) {
      // Reply: rechtsbündig
      return (
        <div key={note.id} className="group flex">
          <div className="ml-auto max-w-[80%]">
            <div className="flex items-end gap-2 flex-row-reverse">
              {/* Avatar */}
              <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold ${
                own
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
              }`}>
                {getInitials(note.vorname, note.nachname)}
              </div>

              <div className="min-w-0">
                {/* Name + Time - rechtsbündig */}
                <div className="flex items-baseline gap-1.5 mb-0.5 justify-end">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {formatTime(note.created_at)}
                  </span>
                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                    {note.vorname} {note.nachname}
                  </span>
                </div>

                {/* Bubble */}
                <div className="flex justify-end">
                  <div className="inline-block rounded-2xl rounded-br-md px-3.5 py-2 bg-primary-600 text-white">
                    <p className="text-sm whitespace-pre-wrap break-words text-left">
                      {note.content}
                    </p>
                  </div>
                </div>

                {/* Page context */}
                {note.page_context && (
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 text-right">
                    auf {note.page_context}
                  </p>
                )}

                {/* Delete - on hover */}
                {own && (
                  <div className="flex justify-end mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="inline-flex items-center text-[11px] text-gray-400 hover:text-error-500 dark:hover:text-error-400"
                      title="Löschen"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Main note: linksbündig
    return (
      <div key={note.id} className="group flex pr-6">
        <div className="max-w-[80%]">
          <div className="flex items-end gap-2">
            {/* Avatar */}
            <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold ${
              own
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
            }`}>
              {getInitials(note.vorname, note.nachname)}
            </div>

            <div className="min-w-0">
              {/* Name + Time */}
              <div className="flex items-baseline gap-1.5 mb-0.5">
                <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                  {note.vorname} {note.nachname}
                </span>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {formatTime(note.created_at)}
                </span>
              </div>

              {/* Bubble */}
              <div className="inline-block rounded-2xl rounded-bl-md px-3.5 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                <p className="text-sm whitespace-pre-wrap break-words">
                  {note.content}
                </p>
              </div>

              {/* Page context */}
              {note.page_context && (
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                  auf {note.page_context}
                </p>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 mt-0.5">
                <button
                  onClick={() => { setReplyingTo(note.id); setReplyMessage(''); }}
                  className="inline-flex items-center gap-1 text-[11px] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  <ChatBubbleLeftIcon className="h-3 w-3" />
                  Antworten
                </button>
                {own && (
                  <button
                    onClick={() => handleDelete(note.id)}
                    className="inline-flex items-center text-[11px] text-gray-400 hover:text-error-500 dark:hover:text-error-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Löschen"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render a thread (main note + its replies)
  const renderThread = (note: Note) => {
    const replies = note.replies || [];

    return (
      <div key={note.id} className="py-3 space-y-2">
        {/* Main note - left */}
        {renderBubble(note, false)}

        {/* Replies - right */}
        {replies.map(reply => renderBubble(reply, true))}

        {/* Inline reply input */}
        {replyingTo === note.id && (
          <div className="pl-10 flex gap-2">
            <textarea
              ref={replyTextareaRef}
              value={replyMessage}
              onChange={e => setReplyMessage(e.target.value)}
              onKeyDown={e => handleKeyDown(e, () => handleReply(note.id))}
              placeholder="Antwort..."
              rows={1}
              className="flex-1 resize-none rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
            <button
              onClick={() => handleReply(note.id)}
              disabled={!replyMessage.trim() || sending}
              className="self-end p-2 rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setReplyingTo(null)}
              className="self-end p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Vorstand-Nachrichten</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 divide-y divide-gray-100 dark:divide-gray-700/30">
          {notes.length > 0 ? (
            notes.map(note => renderThread(note))
          ) : (
            <div className="p-8 text-center">
              <ChatBubbleLeftIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Noch keine Nachrichten</p>
            </div>
          )}
        </div>

        {/* New Message Input */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-3">
          <div className="flex gap-2">
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyDown={e => handleKeyDown(e, handleSend)}
              placeholder="Neue Nachricht..."
              rows={2}
              className="flex-1 resize-none rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="self-end p-2.5 rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BoardNotesPanel;
